<template>
    <div class="comic-question page">
        <wrapper column :backgroundImage="require('@/assets/main-bg.jpg')">
            <div class="main">
                <div class="question-container">
                    <div class="description">{{ question.description }}</div>
                    <div class="mascot">
                        <template v-if="answerStatus === true">
                            <div class="dialog yes">答對了！你真棒！</div>
                            <img
                                width="180px"
                                height="228px"
                                src="@/assets/Stage/Comics/question/mascot-yes.png"
                            />
                        </template>
                        <template v-else-if="answerStatus === false">
                            <div class="dialog no">答錯了⋯再接再厲～</div>
                            <img
                                width="180px"
                                height="228px"
                                src="@/assets/Stage/Comics/question/mascot-no.png"
                            />
                        </template>
                        <template v-else>
                            <img
                                width="207px"
                                src="@/assets/Stage/Comics/question/mascot.png"
                            />
                        </template>
                    </div>
                </div>
                <div class="options">
                    <div
                        class="option"
                        :class="optionClass(i)"
                        v-for="(option, i) in question.options"
                        :key="'option-' + i"
                        @click="answer(i)"
                    >
                        {{ option }}
                    </div>
                </div>
            </div>
            <transition name="fade">
                <comic-result
                    v-if="explainState"
                    :correct="answerStatus === true"
                    :explanation="explainDetail"
                    :showActions="isLastExplain"
                    @back="back"
                    @detail="viewDetail"
                />
            </transition>
            <comic-analysis ref="analysis" :level="level" @close="back" />
        </wrapper>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import LevelConfig from '@/assets/Stage/Comics/level';
import PageGeneral from '@/mixins/PageGeneral';
import ComicResult from '@/components/Comics/ComicResult.vue';
import ComicAnalysis from '@/components/Comics/ComicAnalysis.vue';

export default {
    mixins: [PageGeneral],
    components: { ComicResult, ComicAnalysis },
    data: () => ({
        questionIndex: 0,
        answerStatus: null,
        explainState: false,
    }),
    computed: {
        level() {
            return this.$route.params.level;
        },
        questions() {
            return LevelConfig[this.level] || [];
        },
        question() {
            return this.questions[this.questionIndex];
        },
        optionClass() {
            return (optionIndex) => (this.answerStatus !== null ? {
                yes: optionIndex === this.question.answer,
                no: optionIndex !== this.question.answer,
            } : null);
        },
        explainDetail() {
            return this.question.explain;
        },
        isLastExplain() {
            const isLast = this.questionIndex === this.questions.length - 1;
            return isLast;
        },
        isNextExplainDifferent() {
            if (this.isLastExplain) return false;
            return this.questions[this.questionIndex + 1]?.explain !== this.explainDetail;
        },
    },
    methods: {
        ...mapActions('comics', ['recordAnswer', 'submitAnswer']),
        answer(optionIndex) {
            if (this.answerStatus !== null) return;
            this.answerStatus = this.question.answer === optionIndex;
            this.recordAnswer({
                id: `${this.level}-${this.questionIndex + 1}`, correct: this.answerStatus,
            });
            if (this.questions.length > (this.questionIndex + 1)) {
                if (this.isNextExplainDifferent) {
                    this.showExplainWithDelay().then(() => {
                        setTimeout(() => {
                            this.explainState = false;
                            this.showNextQuestionWithDelay(0);
                        }, 3000);
                    });
                } else {
                    this.showNextQuestionWithDelay();
                }
            } else {
                this.showExplainWithDelay();
                this.submitAnswer(this.level);
            }
        },
        showExplainWithDelay(delay = 3000) {
            return new Promise(((resolve) => {
                setTimeout(() => {
                    this.explainState = true;
                    resolve();
                }, delay);
            }));
        },
        showNextQuestionWithDelay(delay = 3000) {
            setTimeout(() => {
                this.questionIndex += 1;
                this.answerStatus = null;
            }, delay);
        },
        viewDetail() {
            this.$refs.analysis.show();
        },
        back() {
            this.$router.replace({ name: 'Stage_Comics_Menu' });
        },
        devShortcuts(event) {
            if (event.key === 'ArrowRight') {
                if (this.questions.length > this.questionIndex + 1) this.questionIndex += 1;
                else {
                    const newLevel = parseInt(this.level, 10) + 1;
                    this.$router.replace({ params: { level: newLevel === 9 ? 1 : newLevel } });
                    this.questionIndex = 0;
                }
            } else if (event.key === 'ArrowLeft') {
                if (this.questionIndex > 0) this.questionIndex -= 1;
                else {
                    const newLevel = parseInt(this.level, 10) - 1;
                    this.$router.replace({ params: { level: newLevel === 0 ? 8 : newLevel } });
                    this.questionIndex = this.questions.length - 1;
                }
            } else if (event.key === 'ArrowUp') {
                this.answerStatus = !this.answerStatus;
            } else if (event.key === 'ArrowDown') {
                this.answerStatus = null;
            }
        },
    },
    created() {
        if (process.env.NODE_ENV === 'DEV') {
            window.addEventListener('keydown', this.devShortcuts, false);
        }
    },
    destroyed() {
        if (process.env.NODE_ENV === 'DEV') {
            window.removeEventListener('keydown', this.devShortcuts);
        }
    },
};
</script>

<style lang="scss" scoped>
@import "@/scss/main.scss";
.comic-question {
    $main-color: #cacfe5;
    .main {
        color: #235194;
        max-height: 100%;
        -webkit-text-size-adjust: auto;
        @media screen and (max-height: 750px) {
            .mascot {
                zoom: 0.8;
            }
        }
        @media screen and (max-height: 680px) {
            .question-container {
                zoom: 0.9;
            }
            .mascot {
                zoom: 0.9;
            }
        }
        @media screen and (max-height: 610px) {
            zoom: 0.9;
        }
        @media screen and (max-height: 590px) {
            zoom: 0.8;
        }
        .question-container {
            flex: 1;
            overflow: hidden;
            flex-shrink: 1;
            width: 100%;
            padding: 20px 20px 0 20px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: flex-end;
            .description {
                width: 100%;
                height: 220px;
                background-color: $main-color;
                border-radius: 60px;
                padding: 30px 24px;
                //
                display: flex;
                align-items: center;
                // text
                font-size: 30px;
                font-weight: bold;
                line-height: 1.33;
                text-align: justify;
                text-align-last: start;
            }
            .mascot {
                margin-top: 26px;
                height: 312px;
                text-align: center;
                display: flex;
                flex-direction: column;
                justify-content: flex-end;
                align-items: center;
                .dialog {
                    width: 283px;
                    height: 80px;
                    background-size: 100%;
                    font-size: 25px;
                    padding: 8px;
                    &.yes {
                        color: #3fa472;
                        background-image: url("~@/assets/Stage/Comics/question/dialog-yes.png");
                    }
                    &.no {
                        color: #ffa250;
                        background-image: url("~@/assets/Stage/Comics/question/dialog-no.png");
                    }
                }
            }
        }
        .options {
            width: 100%;
            height: 178px;
            display: flex;
            justify-content: center;
            gap: 15px;
            padding: 12px;
            background: $main-color;
            img {
                width: 180px;
            }
            .option {
                padding: 8px;
                display: flex;
                align-items: center;
                justify-content: center;
                height: 152px;
                width: 152px;
                // word-break: keep-all;
                white-space: pre-line;

                font-size: 25px;
                font-weight: 500;
                text-align: center;

                border-radius: 30px;
                box-shadow: -1px 2px 10px 0 #164f99;
                background-color: white;
                &.yes {
                    border: 5px solid #5fd69b;
                    position: relative;
                    &::before {
                        background-image: url("~@/assets/Stage/Comics/question/mark-yes.png");
                    }
                }
                &.no {
                    border: 5px solid #ffa250;
                    position: relative;
                    &::before {
                        background-image: url("~@/assets/Stage/Comics/question/mark-no.png");
                    }
                }
                &.yes,
                &.no {
                    &::before {
                        content: " ";
                        position: absolute;
                        top: -10px;
                        left: -10px;
                        width: 34px;
                        height: 34px;
                        background-size: 100%;
                    }
                }
            }
        }
    }
}
</style>
